import { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, LifeBuoy, ArrowRight } from 'lucide-react';
import { notify } from '../../components/Notify';
import { loginEmailSenhaApi } from '../../api';
import { setToken, setUser } from '../../auth/storage';
import { validateEmail } from '../../validators';
import { abrirWhatsappSuporte, EMAIL_SUPORTE } from '../../config';
import './login.css';

const LEMBRAR_KEY = 'ueb_login_email';

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState(
        location.state?.prefilledEmail || localStorage.getItem(LEMBRAR_KEY) || ''
    );
    const [password, setPassword] = useState('');
    const [showPass, setShowPass] = useState(false);
    const [lembrar, setLembrar] = useState(!!localStorage.getItem(LEMBRAR_KEY));
    const [erros, setErros] = useState({});
    const [enviando, setEnviando] = useState(false);

    const validar = () => {
        const e = {};
        const ve = validateEmail(email);
        if (!ve.valid) e.email = ve.error;
        if (!password || password.length < 6) e.password = 'Mínimo de 6 caracteres.';
        setErros(e);
        return Object.keys(e).length === 0;
    };

    const entrar = async (ev) => {
        ev.preventDefault();
        if (enviando || !validar()) return;
        setEnviando(true);
        try {
            const res = await loginEmailSenhaApi({ email, password });
            const token = res?.data?.token;
            if (!token) return notify('Não foi possível entrar. Tente novamente.');
            setToken(token);
            if (res.data.user) setUser(res.data.user);
            if (lembrar) localStorage.setItem(LEMBRAR_KEY, email);
            else localStorage.removeItem(LEMBRAR_KEY);
            sessionStorage.setItem('ueb_email_prompt_pending', '1');
            navigate('/');
        } catch (err) {
            notify(err?.response?.data?.error || 'Email ou senha incorretos.');
        } finally {
            setEnviando(false);
        }
    };

    const ano = new Date().getFullYear();

    return (
        <div className="uebl">
            <header className="uebl-top">
                <div className="uebl-top-in">
                    <div className="uebl-brand">
                        <Link to="/" aria-label="Voltar ao início">
                            <img className="uebl-logo-img" src="/ueblogo.png" alt="UEB — Carteirinha de Estudante" />
                        </Link>
                    </div>
                    <button type="button" className="uebl-help" onClick={abrirWhatsappSuporte}>
                        <LifeBuoy size={16} /> Ajuda
                    </button>
                </div>
            </header>

            <main className="uebl-main">
                <form className="uebl-card" onSubmit={entrar}>
                    <span className="uebl-kicker">Carteira de Estudante</span>
                    <h1 className="uebl-title">Entrar</h1>
                    <p className="uebl-sub">Bem-vindo de volta. Acesse sua carteira digital.</p>

                    <div className={`uebl-fld ${erros.email ? 'is-error' : ''}`}>
                        <label className="uebl-lbl" htmlFor="email">Email</label>
                        <div className="uebl-line">
                            <input
                                id="email"
                                className="uebl-input"
                                type="email"
                                autoComplete="email"
                                placeholder="voce@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={() => { const v = validateEmail(email); setErros((s) => ({ ...s, email: v.valid ? undefined : v.error })); }}
                            />
                        </div>
                        {erros.email && <span className="uebl-err">{erros.email}</span>}
                    </div>

                    <div className={`uebl-fld ${erros.password ? 'is-error' : ''}`}>
                        <label className="uebl-lbl" htmlFor="password">Senha</label>
                        <div className="uebl-line">
                            <input
                                id="password"
                                className="uebl-input"
                                type={showPass ? 'text' : 'password'}
                                autoComplete="current-password"
                                placeholder="Sua senha"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <button
                                type="button"
                                className="uebl-eye"
                                onClick={() => setShowPass((s) => !s)}
                                aria-label={showPass ? 'Ocultar senha' : 'Mostrar senha'}
                                tabIndex={-1}
                            >
                                {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        {erros.password && <span className="uebl-err">{erros.password}</span>}
                    </div>

                    <div className="uebl-row">
                        <label className="uebl-remember">
                            <input type="checkbox" checked={lembrar} onChange={(e) => setLembrar(e.target.checked)} />
                            Lembrar de mim
                        </label>
                        <Link className="uebl-link" to="/recuperar-acesso">Esqueci minha senha</Link>
                    </div>

                    <button className="uebl-submit" type="submit" disabled={enviando}>
                        {enviando ? <span className="pspin" /> : (<>Entrar <ArrowRight size={18} /></>)}
                    </button>

                    <div className="uebl-sep" />
                    <p className="uebl-foot">
                        Ainda não tem conta? <Link className="uebl-link uebl-link--strong" to="/registrar">Criar conta</Link>
                    </p>
                </form>
            </main>

            <footer className="uebl-bottom">
                <div className="uebl-bottom-in">
                    <span>© {ano} UEB · Carteirinha de Estudante</span>
                    <div className="uebl-bottom-links">
                        <button type="button" className="uebl-linkbtn" onClick={abrirWhatsappSuporte}>Suporte</button>
                        <a href={`mailto:${EMAIL_SUPORTE}`}>{EMAIL_SUPORTE}</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}
